import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Users, Loader2, UserMinus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

/**
 * ReassignApporteur - Permet aux VP de réattribuer un prospect à un autre apporteur
 * 
 * Règles:
 * 1. Seuls les VP associés au dossier peuvent réattribuer
 * 2. Confirmation obligatoire
 * 3. Blocage si le VP a déjà été facturé pour ce prospect
 */
export function ReassignApporteur({ prospect, onReassigned }) {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [teamApporteurs, setTeamApporteurs] = useState([]);
    const [selectedApporteurId, setSelectedApporteurId] = useState('');
    const [confirmationData, setConfirmationData] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [leaveParrainage, setLeaveParrainage] = useState(false);
    const isParrainageDossier = prospect?.origin === 'parrainage';
    const selectedTarget = teamApporteurs.find(a => a.id === selectedApporteurId);
    const targetIsClassic = selectedTarget && selectedTarget.type !== 'parrain';
    // Parrainage non-Coop vers un apporteur : la sortie du parrainage est automatique côté serveur.
    const autoLeave = isParrainageDossier && !prospect?.coop_mode_enabled && selectedTarget?.type !== 'vp_payeur' && selectedTarget?.type !== 'parrain';
    const showLeaveSwitch = isParrainageDossier && targetIsClassic && !autoLeave;
    const willLeave = isParrainageDossier && targetIsClassic && (autoLeave || leaveParrainage);

    const isVP = user?.role === 'vp';
    const isAdmin = user?.is_admin || ['admin', 'admin_full'].includes(user?.role);
    const isVPOfDossier = prospect?.dossier_vp_id === user?.id || prospect?.dossier_vp_2_id === user?.id;
    // Un dossier gagné ne peut plus être basculé vers un parrain (règle backend).
    const EP_WON = ['ep_gagne', 'ep_questionnaire_chantier'];
    const CH_WON = ['ch_termine', 'ch_attente_paiement', 'ch_paiement_effectue', 'ch_valide'];
    const isWon = EP_WON.includes(prospect?.easy_power_stage) || CH_WON.includes(prospect?.chantier_stage);

    // Ne pas afficher si pas VP du dossier
    if (!isAdmin && (!isVP || !isVPOfDossier)) {
        return null;
    }

    // Charger les apporteurs de l'équipe de l'apporteur actuel (via l'endpoint dédié)
    const loadTeamApporteurs = async () => {
        setLoading(true);
        try {
            // Utiliser l'endpoint dédié qui filtre par équipe de l'apporteur actuel
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospect.id}/available-apporteurs`, 
                {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('valnexo_token')}` }
                }
            );
            
            if (!res.ok) {
                throw new Error('Erreur lors du chargement');
            }
            
            const data = await res.json();
            setTeamApporteurs(data.apporteurs || []);
            
            // Si aucun apporteur disponible, afficher un message
            if ((data.apporteurs || []).length === 0 && data.message) {
                toast.info(data.message);
            }
        } catch (err) {
            console.error('Erreur chargement équipe:', err);
            toast.error('Erreur lors du chargement des apporteurs');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenChange = (isOpen) => {
        setOpen(isOpen);
        if (isOpen) {
            loadTeamApporteurs();
            setSelectedApporteurId('');
            setConfirmationData(null);
        }
    };

    // Première étape: demander confirmation
    const handleRequestConfirmation = async () => {
        if (!selectedApporteurId) {
            toast.error('Sélectionnez un apporteur');
            return;
        }

        setConfirming(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospect.id}/reassign-apporteur`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('valnexo_token')}`
                    },
                    body: JSON.stringify({
                        new_apporteur_id: selectedApporteurId,
                        leave_parrainage: willLeave,
                        confirm: false  // Demander confirmation d'abord
                    })
                }
            );

            const data = await res.json();

            if (!res.ok) {
                // Erreur (facture existante, etc.)
                if (data.detail?.error === 'already_invoiced') {
                    toast.error(data.detail.message, { duration: 8000 });
                } else if (data.detail?.error === 'commission_locked') {
                    toast.error(data.detail.message, { duration: 8000 });
                } else {
                    toast.error(data.detail?.message || data.detail || 'Erreur lors de la vérification');
                }
                return;
            }

            if (data.requires_confirmation) {
                // Afficher la confirmation
                setConfirmationData(data);
            }
        } catch (err) {
            console.error('Erreur:', err);
            toast.error('Erreur lors de la vérification');
        } finally {
            setConfirming(false);
        }
    };

    // Deuxième étape: confirmer la réattribution
    const handleConfirmReassign = async () => {
        setConfirming(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospect.id}/reassign-apporteur`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('valnexo_token')}`
                    },
                    body: JSON.stringify({
                        new_apporteur_id: selectedApporteurId,
                        leave_parrainage: willLeave,
                        confirm: true  // Confirmer cette fois
                    })
                }
            );

            const data = await res.json();

            if (!res.ok) {
                toast.error(data.detail?.message || data.detail || 'Erreur lors de la réattribution');
                return;
            }

            toast.success('Dossier réattribué avec succès');
            setOpen(false);
            if (onReassigned) onReassigned();
        } catch (err) {
            console.error('Erreur:', err);
            toast.error('Erreur lors de la réattribution');
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button 
                    variant="outline" 
                    size="sm"
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                    data-testid="reassign-apporteur-btn"
                >
                    <Users className="w-4 h-4 mr-1" />
                    Réattribuer
                </Button>
            </DialogTrigger>
            <DialogContent data-testid="reassign-apporteur-dialog" className="sm:max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-purple-600" />
                        Réattribuer le dossier
                    </DialogTitle>
                    <DialogDescription>
                        Transférer ce dossier à un apporteur, un VP payeur supervisé, ou un parrain de votre réseau.
                    </DialogDescription>
                </DialogHeader>

                {/* Contexte actuel du dossier */}
                {(prospect.dossier_apporteur_name || prospect.origin === 'parrainage') && (
                    <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg flex-wrap">
                        <UserMinus className="w-4 h-4 text-slate-500 shrink-0" />
                        <span className="text-sm text-slate-600">
                            {prospect.origin === 'parrainage' ? 'Parrain actuel :' : 'Attribution actuelle :'}
                        </span>
                        <Badge variant="secondary" className="max-w-full truncate">
                            {prospect.dossier_apporteur_name || 'VP direct'}
                        </Badge>
                    </div>
                )}
                {!prospect.dossier_apporteur_name && prospect.origin !== 'parrainage' && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <Users className="w-4 h-4 text-amber-600 shrink-0" />
                        <span className="text-sm text-amber-800">
                            Dossier en <strong>VP direct</strong> — tu peux l&apos;assigner à un apporteur, un VP payeur ou un parrain.
                        </span>
                    </div>
                )}

                {/* Sélection nouvel apporteur */}
                {!confirmationData && (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium flex items-center gap-2">
                                <UserPlus className="w-4 h-4 text-purple-500" />
                                Nouvel apporteur
                            </label>
                            {loading ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                </div>
                            ) : teamApporteurs.length === 0 ? (
                                <div className="p-3 bg-amber-50 rounded-lg text-amber-700 text-sm">
                                    Aucun apporteur ou VP payeur disponible dans vos équipes
                                </div>
                            ) : (
                                <Select value={selectedApporteurId} onValueChange={setSelectedApporteurId}>
                                    <SelectTrigger data-testid="select-new-apporteur">
                                        <SelectValue placeholder="Choisir un membre..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {teamApporteurs.map((apporteur) => (
                                            <SelectItem key={apporteur.id} value={apporteur.id} disabled={apporteur.type === 'parrain' && isWon}>
                                                <span className="flex items-center gap-2 flex-wrap">
                                                    {apporteur.first_name} {apporteur.last_name}
                                                    {apporteur.type === 'vp_payeur' && (
                                                        <Badge variant="secondary" className="text-[10px] bg-purple-100 text-purple-700">VP Payeur</Badge>
                                                    )}
                                                    {apporteur.type === 'parrain' && (
                                                        <Badge variant="secondary" className="text-[10px] bg-violet-100 text-violet-700">Parrain</Badge>
                                                    )}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            {isWon && teamApporteurs.some(a => a.type === 'parrain') && (
                                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                                    ⚠️ Ce dossier est gagné : bascule vers un parrain désactivée (règle métier).
                                </p>
                            )}
                            {showLeaveSwitch && (
                                <label className="flex items-start gap-2 text-xs text-slate-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-2 cursor-pointer" data-testid="leave-parrainage-toggle">
                                    <input type="checkbox" className="mt-0.5" checked={leaveParrainage} disabled={isWon}
                                        onChange={(e) => setLeaveParrainage(e.target.checked)} data-testid="leave-parrainage-checkbox" />
                                    <span>
                                        <strong>Sortir du Réseau Parrainage</strong> — le parrain est retiré, le dossier devient
                                        {selectedTarget?.type === 'vp_payeur' ? ' VP direct' : ' apporteur classique'} et les commissions sont recalculées.
                                        {!leaveParrainage && (prospect?.coop_mode_enabled
                                            ? " Décoché : on change seulement l'apporteur Coop, le parrain est conservé."
                                            : " Décoché : on change seulement le VP payeur, le parrain est conservé.")}
                                    </span>
                                </label>
                            )}
                            {isParrainageDossier && targetIsClassic && willLeave && isWon && (
                                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="leave-parrainage-won">
                                    ⚠️ Un module de ce dossier est gagné : impossible de le sortir du parrainage. Demandez à un administrateur.
                                </p>
                            )}
                            {autoLeave && !isWon && (
                                <p className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1.5" data-testid="leave-parrainage-auto">
                                    Ce dossier quittera le Réseau Parrainage : parrain retiré, commissions recalculées pour le nouvel apporteur.
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Confirmation */}
                {confirmationData && (
                    <div className="space-y-4">
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                                <div className="space-y-2">
                                    <p className="font-medium text-amber-800">
                                        {confirmationData.message}
                                    </p>
                                    <div className="text-sm text-amber-700 space-y-1">
                                        <p>
                                            <span className="text-slate-500">De:</span>{' '}
                                            <strong>{confirmationData.details?.old_apporteur}</strong>
                                        </p>
                                        <p>
                                            <span className="text-slate-500">Vers:</span>{' '}
                                            <strong>{confirmationData.details?.new_apporteur}</strong>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button 
                        variant="outline" 
                        onClick={() => setOpen(false)}
                        disabled={confirming}
                    >
                        Annuler
                    </Button>
                    
                    {!confirmationData ? (
                        <Button
                            onClick={handleRequestConfirmation}
                            disabled={confirming || !selectedApporteurId || teamApporteurs.length === 0 || (willLeave && isWon)}
                            data-testid="request-confirmation-btn"
                        >
                            {confirming ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : null}
                            Vérifier
                        </Button>
                    ) : (
                        <Button
                            onClick={handleConfirmReassign}
                            disabled={confirming}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="confirm-reassign-btn"
                        >
                            {confirming ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : null}
                            Oui, réattribuer
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ReassignApporteur;
